/**
 * Reading a CSV well enough to show it.
 *
 * Not a general parser — a viewer's job is narrower and its failure mode is
 * different. It must never silently mangle a row, because a table that looks
 * right and has its columns shifted by one is worse than a table that refuses
 * to draw. So quoting is handled properly (the reporting stage writes region
 * names with commas in them) and anything it cannot make sense of is reported
 * rather than guessed at.
 */

export interface Table {
  header: string[];
  rows: string[][];
  /** Rows whose field count differs from the header's. */
  ragged: number;
  /** True when the file was cut short by the read limit. */
  truncated: boolean;
}

/** One line, respecting quotes and doubled quotes inside them. */
export function parseRow(line: string, delimiter = ","): string[] {
  const out: string[] = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const c = line[i];
    if (quoted) {
      if (c === '"') {
        if (line[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      quoted = true;
    } else if (c === delimiter) {
      out.push(field);
      field = "";
    } else {
      field += c;
    }
  }
  out.push(field);
  return out;
}

/**
 * The delimiter this file actually uses.
 *
 * OpenADS writes commas, but a table exported from a European locale uses
 * semicolons, and drawing one of those as a single column per row is the kind
 * of quiet wrongness this module exists to avoid.
 */
function delimiterOf(firstLine: string): string {
  const counts = [",", ";", "\t"].map((d) => [d, parseRow(firstLine, d).length] as const);
  const best = counts.reduce((a, b) => (b[1] > a[1] ? b : a));
  return best[1] > 1 ? best[0] : ",";
}

export function parseCsv(text: string, truncated = false): Table {
  const lines = text
    .split(/\r?\n/)
    // A trailing newline is not a row; a blank line inside the file is not
    // either, and the reporting stage writes one at the end of every table.
    .filter((l) => l.trim().length > 0);
  if (lines.length === 0) {
    return { header: [], rows: [], ragged: 0, truncated };
  }
  const delimiter = delimiterOf(lines[0]);
  const header = parseRow(lines[0], delimiter);
  const rows = lines.slice(1).map((l) => parseRow(l, delimiter));
  const ragged = rows.filter((r) => r.length !== header.length).length;
  return { header, rows, ragged, truncated };
}

/** Right-align a column when every value in it reads as a number. */
export function numericColumns(table: Table): boolean[] {
  return table.header.map((_, i) => {
    const values = table.rows.map((r) => r[i]).filter((v) => v != null && v.trim() !== "");
    if (values.length === 0) return false;
    return values.every((v) => Number.isFinite(Number(v)));
  });
}
